import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'confirm';
export type ButtonSize = 'sm' | 'md' | 'lg';

/**
 * Buttons carry weight through fill and border, not colour: only `confirm` and
 * `danger` are allowed a saturated hue, because both commit real money.
 */
const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-accent-600 text-fg-inverse border border-accent-600 ' +
    'hover:bg-accent-700 hover:border-accent-700 active:bg-accent-900 active:border-accent-900',
  secondary:
    'bg-surface text-fg border border-line-strong ' +
    'hover:bg-sunken hover:border-fg-faint active:border-fg-muted',
  ghost:
    'bg-transparent text-fg-secondary border border-transparent ' +
    'hover:bg-sunken hover:text-fg active:border-line',
  danger:
    'bg-surface text-danger-500 border border-danger-500 ' +
    'hover:bg-danger-50 active:bg-danger-50 active:border-danger-700',
  confirm:
    'bg-ok-700 text-fg-inverse border border-ok-700 ' +
    'hover:bg-ok-500 hover:border-ok-500 active:bg-ok-700',
};

/**
 * Every size is at least 44px tall on touch and relaxes to the dense desktop
 * height at `lg`. The label size never changes with the breakpoint — only the
 * box around it — so nothing shrinks to make room for a bigger target.
 */
const SIZES: Record<ButtonSize, string> = {
  sm: 'h-11 px-3 text-xs gap-1.5 lg:h-7 lg:px-2.5',
  md: 'h-11 px-4 text-base gap-2 lg:h-9 lg:px-3.5',
  lg: 'h-11 px-5 text-md gap-2',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  fullWidth?: boolean;
  /** Rendered before the label at the same optical size as the text. */
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    isLoading = false,
    fullWidth = false,
    icon,
    className,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = disabled === true || isLoading;

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={isLoading || undefined}
      className={cn(
        'focus-ring inline-flex select-none items-center justify-center whitespace-nowrap',
        'rounded-md font-medium transition-colors duration-fast ease-out',
        'disabled:cursor-not-allowed disabled:opacity-45',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {isLoading ? (
        <Loader2
          size={size === 'sm' ? 13 : 15}
          strokeWidth={1.5}
          aria-hidden="true"
          className="motion-safe:[animation:spin_900ms_linear_infinite]"
        />
      ) : (
        icon
      )}
      {children}
    </button>
  );
});
